import * as THREE from 'three'
import { SogsGpuPackerGPU } from '@/plugins/gsplat/gpu/SogsGpuPackerGPU'
import type { SogsMetaV2 } from '@/plugins/gsplat/loaders/SogsParser'
// 导入着色器
import gsplatSogsCentersFS from '@/plugins/gsplat/shaders/frag/gsplatSogsCenters.fs'
import gsplatSogsReorderShFS from '@/plugins/gsplat/shaders/frag/gsplatSogsReorderSh.fs'
import { registerGSplatChunks } from '@/plugins/gsplat/shaders/gsplat-chunks'
import { syncTextureUploadVersion } from '@/plugins/gsplat/utils/syncTextureUploadVersion'
import { getTextureImage } from '@/plugins/gsplat/utils/textureImage'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('gsplat:core')

const GPU_SYNC_TIMEOUT_MS = 30_000

interface SogsTextureRestoreSource {
  blob: Blob
  width: number
  height: number
}

const textureRestoreSources = new WeakMap<THREE.Texture, SogsTextureRestoreSource>()

/** 同一 Renderer 的 context restore 纹理解码严格串行，避免所有驻留 LOD 同时重建峰值。 */
const sourceTextureRestoreTailsByRenderer = new WeakMap<THREE.WebGLRenderer, Promise<void>>()

function runSourceTextureRestoreExclusive(
  renderer: THREE.WebGLRenderer,
  run: () => Promise<void>
): Promise<void> {
  const previous = sourceTextureRestoreTailsByRenderer.get(renderer) ?? Promise.resolve()
  const queued = previous.catch(() => undefined).then(run)
  const tail = queued.catch(() => undefined)
  sourceTextureRestoreTailsByRenderer.set(renderer, tail)
  void tail.finally(() => {
    if (sourceTextureRestoreTailsByRenderer.get(renderer) === tail) {
      sourceTextureRestoreTailsByRenderer.delete(renderer)
    }
  })
  return queued
}

/** 由 Bundle loader 为可释放解码背板的纹理登记压缩恢复源。 */
export function registerGSplatSogsTextureRestoreSource(
  texture: THREE.Texture,
  blob: Blob,
  width: number,
  height: number
): void {
  textureRestoreSources.set(texture, { blob, width, height })
}

function createAbortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw createAbortError()
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

function createGpuSyncTimeoutError(): Error {
  const error = new Error(`GPU center readback timed out after ${GPU_SYNC_TIMEOUT_MS}ms`)
  error.name = 'TimeoutError'
  return error
}

interface SogsCentersRenderResources {
  material: THREE.ShaderMaterial
  geometry: THREE.BufferGeometry
  scene: THREE.Scene
  camera: THREE.OrthographicCamera
}

const centersRenderResourcesByRenderer = new WeakMap<
  THREE.WebGLRenderer,
  SogsCentersRenderResources
>()

interface CentersGenerationTask {
  signal?: AbortSignal
  run: () => Promise<void>
  resolve: () => void
  reject: (error: unknown) => void
  started: boolean
  onAbort: (() => void) | null
}

interface CentersGenerationQueue {
  active: CentersGenerationTask | null
  pending: CentersGenerationTask[]
  disposeResourcesWhenIdle: boolean
}

/**
 * 中心点 pass 会复用 Renderer 级 material，并切换同一个 WebGLRenderer 的 RenderTarget。
 * 即使资源来自不同 GSplatAssetLoader，也必须按 Renderer 串行，否则 uniforms、PBO 回读和
 * RenderTarget 恢复会相互覆盖。
 */
const centersGenerationQueuesByRenderer = new WeakMap<THREE.WebGLRenderer, CentersGenerationQueue>()

function deleteCentersGenerationQueueIfIdle(
  renderer: THREE.WebGLRenderer,
  queue: CentersGenerationQueue
): void {
  if (
    queue.active === null &&
    queue.pending.length === 0 &&
    centersGenerationQueuesByRenderer.get(renderer) === queue
  ) {
    if (queue.disposeResourcesWhenIdle) {
      disposeCentersRenderResourcesNow(renderer)
    }
    centersGenerationQueuesByRenderer.delete(renderer)
  }
}

function drainCentersGenerationQueue(
  renderer: THREE.WebGLRenderer,
  queue: CentersGenerationQueue
): void {
  if (queue.active) {
    return
  }

  while (queue.pending.length > 0) {
    const task = queue.pending.shift()
    if (!task) {
      break
    }

    if (task.onAbort) {
      task.signal?.removeEventListener('abort', task.onAbort)
      task.onAbort = null
    }

    if (task.signal?.aborted) {
      task.reject(createAbortError())
      continue
    }

    task.started = true
    queue.active = task
    void (async () => {
      try {
        await task.run()
        task.resolve()
      } catch (error) {
        task.reject(error)
      } finally {
        if (queue.active === task) {
          queue.active = null
        }
        drainCentersGenerationQueue(renderer, queue)
      }
    })()
    return
  }

  deleteCentersGenerationQueueIfIdle(renderer, queue)
}

function runCentersGenerationExclusive(
  renderer: THREE.WebGLRenderer,
  signal: AbortSignal | undefined,
  run: () => Promise<void>
): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(createAbortError())
  }

  let queue = centersGenerationQueuesByRenderer.get(renderer)
  if (!queue) {
    queue = { active: null, pending: [], disposeResourcesWhenIdle: false }
    centersGenerationQueuesByRenderer.set(renderer, queue)
  }
  const rendererQueue = queue

  return new Promise<void>((resolve, reject) => {
    const task: CentersGenerationTask = {
      signal,
      run,
      resolve,
      reject,
      started: false,
      onAbort: null,
    }

    if (signal) {
      task.onAbort = () => {
        // 已开始的任务仍持有 Renderer/PBO/RenderTarget。abort 交给任务本身处理，并且必须等
        // 底层 Promise 真正 settle 后才能放行下一项，不能只因 signal 触发就提前解锁。
        if (task.started) {
          return
        }

        const index = rendererQueue.pending.indexOf(task)
        if (index === -1) {
          return
        }
        rendererQueue.pending.splice(index, 1)
        signal.removeEventListener('abort', task.onAbort as () => void)
        task.onAbort = null
        reject(createAbortError())
        deleteCentersGenerationQueueIfIdle(renderer, rendererQueue)
      }
      signal.addEventListener('abort', task.onAbort, { once: true })
    }

    rendererQueue.pending.push(task)
    drainCentersGenerationQueue(renderer, rendererQueue)
  })
}

function closeTextureImage(image: unknown): void {
  try {
    if (image && typeof image === 'object' && 'close' in image) {
      const close = image.close
      if (typeof close === 'function') {
        close.call(image)
      }
    } else if (typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement) {
      image.onload = null
      image.onerror = null
      image.src = ''
    }
  } catch {
    // ImageBitmap.close()/HTMLImageElement reset are best-effort during teardown.
  }
}

function releaseTextureImage(texture: THREE.Texture | null | undefined): void {
  if (!texture) {
    return
  }

  try {
    closeTextureImage(texture.image as unknown)
  } finally {
    texture.image = null
  }
}

/**
 * 释放已经上传到 GPU 的解码图像，同时保留 shader/LOD 后续需要的宽高元数据。
 *
 * 这里不能直接置 null：evalTextureSize 与 WorkBuffer copy pass 仍会读取纹理尺寸。
 */
function releaseTextureImageBacking(texture: THREE.Texture): void {
  const image = texture.image as { width?: number; height?: number } | null | undefined
  const width = Number(image?.width)
  const height = Number(image?.height)
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return
  }

  try {
    closeTextureImage(image)
  } finally {
    texture.image = { width, height }
  }
}

function disposeTextureAndImage(texture: THREE.Texture | null | undefined): void {
  if (!texture) {
    return
  }

  try {
    texture.dispose()
  } finally {
    releaseTextureImage(texture)
  }
}

function disposeCentersRenderResourcesNow(renderer: THREE.WebGLRenderer): void {
  const resources = centersRenderResourcesByRenderer.get(renderer)
  if (!resources) {
    return
  }
  for (const uniform of Object.values(resources.material.uniforms)) {
    uniform.value = null
  }
  resources.material.dispose()
  resources.geometry.dispose()
  resources.scene.clear()
  centersRenderResourcesByRenderer.delete(renderer)
}

/** 释放指定 Renderer 的 SOGS 中心点生成 pass 资源。 */
export function disposeGSplatSogsRenderResources(renderer: THREE.WebGLRenderer): void {
  const queue = centersGenerationQueuesByRenderer.get(renderer)
  if (queue && (queue.active !== null || queue.pending.length > 0)) {
    // PCManager.dispose 会先 abort loader，再释放 renderer 级资源。活动回读真正 settle 前不能
    // 销毁其正在使用的 material/geometry；等队列完全静止后再回收。
    queue.disposeResourcesWhenIdle = true
    return
  }
  disposeCentersRenderResourcesNow(renderer)
}

/**
 * GPU-based SOGS 数据容器
 *
 * 关键差异：
 * - PlayCanvas: 存储 Texture 对象（GPU 纹理）
 * - 现有 SogsData: 存储 Uint8Array（CPU 数据）+ 懒加载 DataTexture
 * - GSplatSogsData: 直接存储 GPU 纹理（从 WebP 加载）
 *
 * 参考 PlayCanvas gsplat-sogs-data.js
 *
 * 注意：这个类与 SogsData 接口不同，不共享基类
 */
export class GSplatSogsData {
  /** SOGS V2 元信息 */
  readonly meta: SogsMetaV2
  /** splat 总数 */
  readonly numSplats: number
  /** 资源 URL，仅用于调试打印 */
  readonly url: string = ''

  /** 位置低位纹理（GPU 端） */
  readonly means_l: THREE.Texture
  /** 位置高位纹理（GPU 端） */
  readonly means_u: THREE.Texture
  /** 旋转四元数纹理 */
  readonly quats: THREE.Texture
  /** 缩放纹理 */
  readonly scales: THREE.Texture
  /** SH DC（0 阶）纹理 */
  readonly sh0: THREE.Texture
  /** SH 高阶聚类中心纹理，无 SH 时为 undefined */
  readonly sh_centroids?: THREE.Texture | undefined
  /** SH 高阶聚类标签纹理，无 SH 时为 undefined */
  readonly sh_labels?: THREE.Texture | undefined

  /** 打包后的几何属性纹理，仅由显式 packGpuMemory 生成 */
  packedTexture: THREE.Texture | null = null
  /** 打包后的 SH0 纹理，仅由显式 packGpuMemory 生成 */
  packedSh0: THREE.Texture | null = null
  /** 打包后的 SHN 纹理，仅由显式 packShMemory 生成 */
  packedShN: THREE.Texture | null = null

  // 缓存的中心点数组（generateCenters 后生成）
  private _centers: Float32Array | null = null

  // 缓存的 SH bands
  private _shBands: number = 0

  /** WebGL 渲染器引用（用于上下文恢复） */
  private renderer: THREE.WebGLRenderer | null = null

  /** 上下文恢复事件处理函数 */
  private _onContextRestored: (() => void) | null = null

  /** GPU 打包器实例（委托给 SogsGpuPackerGPU） */
  private _gpuPacker: SogsGpuPackerGPU | null = null

  /** 防止重复释放，并阻止异步中心点回读在销毁后重新挂回大数组。 */
  private _destroyed = false

  /** LOD 资源完成 GPU 预上传后，解码图像只释放一次。 */
  private _sourceImagesReleased = false

  /** 已释放解码背板的纹理所对应的压缩 WebP，用于 WebGL context restore。 */
  private readonly _sourceTextureRestoreSources = new Map<THREE.Texture, SogsTextureRestoreSource>()

  /** context loss/destroy 通过递增代次取消无法原生 abort 的 createImageBitmap。 */
  private _sourceTextureRestoreGeneration = 0

  /** 同一代恢复合并；新一代会等待旧解码真正 settle 后再开始。 */
  private _sourceTextureRestorePromise: Promise<void> | null = null

  private _activeSourceTextureRestoreGeneration = -1

  /** 让取消/销毁能够立即终止 WebKit Image fallback，并从 Renderer 恢复队列退出。 */
  private _sourceTextureRestoreAbortController: AbortController | null = null

  /**
   * 统一通过 ShaderChunkManager 展开自定义 include，避免手工字符串替换。
   */
  private assembleFragmentShader(source: string): string {
    const manager = registerGSplatChunks()
    return manager.assembleSource(source)
  }

  private async readCenterPixels(
    renderer: THREE.WebGLRenderer,
    renderTarget: THREE.WebGLRenderTarget,
    width: number,
    height: number,
    signal?: AbortSignal
  ): Promise<Float32Array> {
    throwIfAborted(signal)
    const pboPixels = await this.readCenterPixelsWithPbo(
      renderer,
      renderTarget,
      width,
      height,
      signal
    )
    if (pboPixels) {
      return pboPixels
    }

    throwIfAborted(signal)

    const asyncReader = (
      renderer as THREE.WebGLRenderer & {
        readRenderTargetPixelsAsync?: (
          renderTarget: THREE.WebGLRenderTarget,
          x: number,
          y: number,
          width: number,
          height: number,
          buffer: Float32Array
        ) => Promise<Float32Array>
      }
    ).readRenderTargetPixelsAsync

    // Three.js 的异步回读接口没有取消契约。带 signal 的 LOD 加载使用我们可取消的
    // PBO 路径；PBO 不可用时退回同步分块读取，避免 abort 后异步任务继续持有 RenderTarget。
    if (!signal && typeof asyncReader === 'function') {
      const buffer = new Float32Array(width * height * 4)
      try {
        await asyncReader.call(renderer, renderTarget, 0, 0, width, height, buffer)
        return buffer
      } catch (error) {
        log.warn(
          '[GSplatSogsData] async center readback failed, using sync fallback: ' +
            (error instanceof Error ? error.message : String(error))
        )
      }
    }

    return this.readCenterPixelsSync(renderer, renderTarget, width, height, signal)
  }

  private async readCenterPixelsWithPbo(
    renderer: THREE.WebGLRenderer,
    renderTarget: THREE.WebGLRenderTarget,
    width: number,
    height: number,
    signal?: AbortSignal
  ): Promise<Float32Array | null> {
    throwIfAborted(signal)
    const rendererWithContext = renderer as THREE.WebGLRenderer & {
      getContext?: () => WebGLRenderingContext
    }
    if (typeof rendererWithContext.getContext !== 'function') {
      return null
    }

    if (
      renderTarget.texture.format !== THREE.RGBAFormat ||
      renderTarget.texture.type !== THREE.FloatType
    ) {
      return null
    }

    const gl = rendererWithContext.getContext() as WebGL2RenderingContext
    if (
      !gl ||
      typeof gl.fenceSync !== 'function' ||
      typeof gl.clientWaitSync !== 'function' ||
      typeof gl.getBufferSubData !== 'function' ||
      typeof gl.readPixels !== 'function'
    ) {
      return null
    }

    const pbo = gl.createBuffer()
    if (!pbo) {
      return null
    }

    let previousRenderTarget: THREE.WebGLRenderTarget | null = null
    let shouldRestoreRenderTarget = false
    let sync: WebGLSync | null = null

    try {
      const buffer = new Float32Array(width * height * 4)
      previousRenderTarget = renderer.getRenderTarget()
      throwIfAborted(signal)

      shouldRestoreRenderTarget = true
      renderer.setRenderTarget(renderTarget)

      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, pbo)
      gl.bufferData(gl.PIXEL_PACK_BUFFER, buffer.byteLength, gl.STREAM_READ)
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.FLOAT, 0)
      sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0)
      if (!sync) {
        return null
      }

      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null)
      renderer.setRenderTarget(previousRenderTarget)
      shouldRestoreRenderTarget = false

      await this.waitForGpuSync(gl, sync, signal)
      throwIfAborted(signal)

      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, pbo)
      gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, buffer)
      throwIfAborted(signal)
      return buffer
    } catch (error) {
      if (
        signal?.aborted ||
        isAbortError(error) ||
        (error instanceof Error && error.name === 'TimeoutError')
      ) {
        throw signal?.aborted ? createAbortError() : error
      }
      log.warn(
        '[GSplatSogsData] direct PBO center readback failed, using Three fallback: ' +
          (error instanceof Error ? error.message : String(error))
      )
      return null
    } finally {
      try {
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null)
      } catch {
        // Context-loss teardown is best-effort; continue releasing the remaining handles.
      }
      if (shouldRestoreRenderTarget) {
        try {
          renderer.setRenderTarget(previousRenderTarget)
        } catch {
          // Context loss can make target restoration fail; sync/PBO still need deletion.
        }
      }
      if (sync) {
        try {
          gl.deleteSync(sync)
        } catch {
          // Continue to PBO deletion.
        }
      }
      try {
        gl.deleteBuffer(pbo)
      } catch {
        // The context may already be gone.
      }
    }
  }

  private waitForGpuSync(
    gl: WebGL2RenderingContext,
    sync: WebGLSync,
    signal?: AbortSignal
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | null = null
      let settled = false
      const startedAt = Date.now()

      const cleanup = () => {
        if (timer !== null) {
          clearTimeout(timer)
          timer = null
        }
        signal?.removeEventListener('abort', onAbort)
      }
      const settle = (error?: unknown) => {
        if (settled) {
          return
        }
        settled = true
        cleanup()
        if (error === undefined) {
          resolve()
        } else {
          reject(error)
        }
      }
      const onAbort = () => settle(createAbortError())
      const probe = () => {
        timer = null
        if (signal?.aborted) {
          settle(createAbortError())
          return
        }
        if (Date.now() - startedAt >= GPU_SYNC_TIMEOUT_MS) {
          settle(createGpuSyncTimeoutError())
          return
        }

        let result: number
        try {
          result = gl.clientWaitSync(sync, gl.SYNC_FLUSH_COMMANDS_BIT, 0)
        } catch (error) {
          settle(error)
          return
        }
        if (result === gl.WAIT_FAILED) {
          settle(new Error('GPU sync wait failed'))
          return
        }
        if (result === gl.TIMEOUT_EXPIRED) {
          timer = setTimeout(probe, 4)
          return
        }
        settle()
      }

      if (signal?.aborted) {
        settle(createAbortError())
        return
      }
      signal?.addEventListener('abort', onAbort, { once: true })
      timer = setTimeout(probe, 0)
    })
  }

  private readCenterPixelsSync(
    renderer: THREE.WebGLRenderer,
    renderTarget: THREE.WebGLRenderTarget,
    width: number,
    height: number,
    signal?: AbortSignal
  ): Float32Array {
    throwIfAborted(signal)
    const result = new Float32Array(width * height * 4)
    const rowsPerRead = 32
    const reusableBuffer = new Float32Array(width * rowsPerRead * 4)

    for (let y = 0; y < height; y += rowsPerRead) {
      throwIfAborted(signal)
      const readRows = Math.min(rowsPerRead, height - y)
      const readBuffer =
        readRows === rowsPerRead ? reusableBuffer : new Float32Array(width * readRows * 4)

      renderer.readRenderTargetPixels(renderTarget, 0, y, width, readRows, readBuffer)
      result.set(readBuffer, y * width * 4)
    }

    return result
  }

  constructor(params: {
    meta: SogsMetaV2
    numSplats: number
    means_l: THREE.Texture
    means_u: THREE.Texture
    quats: THREE.Texture
    scales: THREE.Texture
    sh0: THREE.Texture
    sh_centroids?: THREE.Texture
    sh_labels?: THREE.Texture
    url?: string
  }) {
    this.meta = params.meta
    this.numSplats = params.numSplats
    this.means_l = params.means_l
    this.means_u = params.means_u
    this.quats = params.quats
    this.scales = params.scales
    this.sh0 = params.sh0
    this.sh_centroids = params.sh_centroids
    this.sh_labels = params.sh_labels
    this.url = params.url || ''

    this.collectTextureRestoreSources()

    // 计算 SH bands
    // 优先从 meta.shN.bands 读取（V2 格式明确指定）
    // 回退：从 sh_centroids 纹理宽度推断（sh palette 有 64 entries per row: 192=1band, 512=2bands, 960=3bands）
    if (this.meta.shN?.bands !== undefined) {
      this._shBands = this.meta.shN.bands
    } else {
      const shBandsWidths: { [key: number]: number } = { 192: 1, 512: 2, 960: 3 }
      const shCentroidsWidth = this.sh_centroids ? getTextureImage(this.sh_centroids).width : 0
      this._shBands = shBandsWidths[shCentroidsWidth] ?? 0
    }
  }

  /**
   * 获取 SH bands 数量
   */
  get shBands(): number {
    return this._shBands
  }

  /**
   * 释放 SOG WebP 的 ImageBitmap/HTMLImageElement 解码背板。
   *
   * 调用方必须先完成纹理预上传；压缩 WebP 会继续保留，用于 WebGL context restore。
   */
  releaseSourceImages(): boolean {
    if (this._destroyed || this._sourceImagesReleased) {
      return this._sourceImagesReleased
    }

    this.collectTextureRestoreSources()
    const textures = this.getSourceTextures()
    if (textures.some((texture) => !this._sourceTextureRestoreSources.has(texture))) {
      return false
    }

    this._sourceImagesReleased = true
    for (const texture of textures) {
      releaseTextureImageBacking(texture)
    }
    return true
  }

  get sourceImagesReleased(): boolean {
    return this._sourceImagesReleased
  }

  /** 取消当前恢复；底层解码 settle 后会关闭迟到的 ImageBitmap。 */
  cancelSourceTextureRestore(): void {
    this._sourceTextureRestoreGeneration++
    this._sourceTextureRestoreAbortController?.abort()
    this._sourceTextureRestoreAbortController = null
  }

  /**
   * 从保留的压缩 WebP 串行恢复原始 SOG 纹理，并在上传后再次释放解码背板。
   */
  restoreSourceTextures(renderer: THREE.WebGLRenderer): Promise<void> {
    if (!this._sourceImagesReleased) {
      return Promise.resolve()
    }
    if (this._destroyed) {
      return Promise.reject(createAbortError())
    }

    const generation = this._sourceTextureRestoreGeneration
    if (
      this._sourceTextureRestorePromise &&
      this._activeSourceTextureRestoreGeneration === generation
    ) {
      return this._sourceTextureRestorePromise
    }

    const previous = this._sourceTextureRestorePromise?.catch(() => undefined)
    const abortController = new AbortController()
    const restore = runSourceTextureRestoreExclusive(renderer, async () => {
      await previous
      this.assertSourceTextureRestoreActive(generation)
      throwIfAborted(abortController.signal)

      for (const texture of this.getSourceTextures()) {
        const source = this._sourceTextureRestoreSources.get(texture)
        if (!source) {
          throw new Error(`[GSplatSogsData] missing texture restore source: ${texture.name}`)
        }

        const image = await this.decodeTextureRestoreSource(source.blob, abortController.signal)
        try {
          this.assertSourceTextureRestoreActive(generation)
          texture.image = image
          texture.needsUpdate = true
          renderer.initTexture(texture)
          syncTextureUploadVersion(renderer, texture)
          this.assertSourceTextureRestoreActive(generation)
        } finally {
          if (texture.image === image) {
            releaseTextureImageBacking(texture)
          } else {
            closeTextureImage(image)
          }
        }
      }
    })

    this._activeSourceTextureRestoreGeneration = generation
    this._sourceTextureRestoreAbortController = abortController
    let tracked: Promise<void>
    tracked = restore.finally(() => {
      if (this._sourceTextureRestorePromise === tracked) {
        this._sourceTextureRestorePromise = null
        this._activeSourceTextureRestoreGeneration = -1
      }
      if (this._sourceTextureRestoreAbortController === abortController) {
        this._sourceTextureRestoreAbortController = null
      }
    })
    this._sourceTextureRestorePromise = tracked
    return tracked
  }

  private assertSourceTextureRestoreActive(generation: number): void {
    if (this._destroyed || generation !== this._sourceTextureRestoreGeneration) {
      throw createAbortError()
    }
  }

  private async decodeTextureRestoreSource(
    blob: Blob,
    signal: AbortSignal
  ): Promise<TexImageSource> {
    throwIfAborted(signal)
    if (typeof createImageBitmap !== 'undefined') {
      try {
        const bitmapPromise = createImageBitmap(blob, {
          colorSpaceConversion: 'none',
          premultiplyAlpha: 'none',
        })
        return await this.waitForImageBitmapDecode(bitmapPromise, signal)
      } catch (error) {
        if (signal.aborted || isAbortError(error)) {
          throw createAbortError()
        }
        // 与初次加载保持一致：个别 WebKit WebP bitmap decode 失败时退回 HTMLImageElement。
      }
    }

    throwIfAborted(signal)
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image()
      const objectUrl = URL.createObjectURL(blob)
      let settled = false
      const cleanup = () => {
        image.onload = null
        image.onerror = null
        signal.removeEventListener('abort', onAbort)
        URL.revokeObjectURL(objectUrl)
      }
      const settle = (error?: unknown) => {
        if (settled) {
          return
        }
        settled = true
        cleanup()
        if (error === undefined) {
          resolve(image)
        } else {
          reject(error)
        }
      }
      const onAbort = () => {
        // WebKit 的 HTMLImageElement 解码没有 AbortSignal；清空 src 会终止其当前加载。
        settle(createAbortError())
        image.src = ''
      }
      image.onload = () => {
        if (signal.aborted) {
          onAbort()
          return
        }
        settle()
      }
      image.onerror = () => {
        settle(new Error('Failed to restore SOG WebP texture'))
        image.src = ''
      }
      signal.addEventListener('abort', onAbort, { once: true })
      image.decoding = 'async'
      image.src = objectUrl
      if (signal.aborted) {
        onAbort()
      }
    })
  }

  /** createImageBitmap 无法硬取消；先释放恢复队列，迟到的 bitmap 只负责 close。 */
  private waitForImageBitmapDecode(
    bitmapPromise: Promise<ImageBitmap>,
    signal: AbortSignal
  ): Promise<ImageBitmap> {
    return new Promise<ImageBitmap>((resolve, reject) => {
      let settled = false
      const cleanup = () => signal.removeEventListener('abort', onAbort)
      const onAbort = () => {
        if (settled) {
          return
        }
        settled = true
        cleanup()
        reject(createAbortError())
      }

      signal.addEventListener('abort', onAbort, { once: true })
      if (signal.aborted) {
        onAbort()
      }

      void bitmapPromise.then(
        (bitmap) => {
          if (settled) {
            closeTextureImage(bitmap)
            return
          }
          settled = true
          cleanup()
          if (signal.aborted) {
            closeTextureImage(bitmap)
            reject(createAbortError())
            return
          }
          resolve(bitmap)
        },
        (error: unknown) => {
          if (settled) {
            return
          }
          settled = true
          cleanup()
          reject(signal.aborted ? createAbortError() : error)
        }
      )
    })
  }

  private getSourceTextures(): THREE.Texture[] {
    const textures = new Set<THREE.Texture>([
      this.means_l,
      this.means_u,
      this.quats,
      this.scales,
      this.sh0,
    ])
    if (this.sh_centroids) {
      textures.add(this.sh_centroids)
    }
    if (this.sh_labels) {
      textures.add(this.sh_labels)
    }
    return Array.from(textures)
  }

  private collectTextureRestoreSources(): void {
    for (const texture of this.getSourceTextures()) {
      if (this._sourceTextureRestoreSources.has(texture)) {
        continue
      }
      const source = textureRestoreSources.get(texture)
      if (source) {
        this._sourceTextureRestoreSources.set(texture, source)
        textureRestoreSources.delete(texture)
      }
    }
  }

  // 调试打印改为按需调用；默认不输出

  /**
   * 标识为 SOGS 数据
   */
  get isSogs(): boolean {
    return true
  }

  /**
   * 计算包围盒，参考 PlayCanvas gsplat-sogs-data.js calcAabb()。
   *
   * @param result - 输出目标，按本地坐标系写入 min/max
   * @returns 固定返回 true
   */
  calcAabb(result: THREE.Box3): boolean {
    const { mins, maxs } = this.meta.means

    // 指数反变换
    const map = (v: number) => Math.sign(v) * (Math.exp(Math.abs(v)) - 1)

    const minX = map(mins[0])
    const minY = map(mins[1])
    const minZ = map(mins[2])
    const maxX = map(maxs[0])
    const maxY = map(maxs[1])
    const maxZ = map(maxs[2])

    result.min.set(minX, minY, minZ)
    result.max.set(maxX, maxY, maxZ)

    return true
  }

  /**
   * 计算内存占用（GPU 纹理估算）。
   *
   * GSplatSogsData 只存储 GPU 纹理，没有 CPU 数据。
   *
   * @returns 源纹理与打包纹理的估算字节数之和
   */
  getMemoryUsage(): number {
    let total = 0

    // 源纹理内存（假设都是 RGBA8，从 WebP 解压后的尺寸）
    const estimateTextureMemory = (texture: THREE.Texture | undefined): number => {
      if (!texture) return 0
      const { width, height } = getTextureImage(texture)
      // RGBA8 = 4 bytes per pixel
      return width * height * 4
    }

    // 源纹理
    total += estimateTextureMemory(this.means_l)
    total += estimateTextureMemory(this.means_u)
    total += estimateTextureMemory(this.quats)
    total += estimateTextureMemory(this.scales)
    total += estimateTextureMemory(this.sh0)
    total += estimateTextureMemory(this.sh_centroids)
    total += estimateTextureMemory(this.sh_labels)

    // 打包纹理
    if (this.packedTexture) {
      // RGBA32UI = 16 bytes per pixel
      const { width, height } = getTextureImage(this.packedTexture)
      total += width * height * 16
    }

    if (this.packedSh0) {
      total += estimateTextureMemory(this.packedSh0)
    }

    if (this.packedShN) {
      total += estimateTextureMemory(this.packedShN)
    }

    return total
  }

  /**
   * 计算未压缩内存占用（假设解压为标准 PLY 格式）。
   *
   * @returns 按每 splat 基础属性 + SH 系数估算的字节数
   */
  getUncompressedMemoryUsage(): number {
    // 每个 splat 的基础属性：
    // - position: 3 * 4 = 12 bytes
    // - rotation: 4 * 4 = 16 bytes
    // - scale: 3 * 4 = 12 bytes
    // - color: 3 * 4 = 12 bytes
    // - opacity: 1 * 4 = 4 bytes
    // 总计：56 bytes
    let bytesPerSplat = 56

    // SH 高阶系数（如果有）
    if (this._shBands > 0) {
      // band 1: 3 * 4 = 12 bytes
      // band 2: 5 * 4 = 20 bytes
      // band 3: 7 * 4 = 28 bytes
      const shSizes = [0, 3, 5, 7]
      for (let i = 1; i <= this._shBands; i++) {
        bytesPerSplat += shSizes[i] * 3 * 4 // RGB * float32
      }
    }

    return this.numSplats * bytesPerSplat
  }

  /**
   * 计算压缩率。
   *
   * @returns 未压缩 / 压缩，压缩尺寸为 0 时返回 0
   */
  getCompressionRatio(): number {
    const compressed = this.getMemoryUsage()
    if (compressed === 0) return 0
    return this.getUncompressedMemoryUsage() / compressed
  }

  /**
   * 计算焦点（用于相机定位），参考 PlayCanvas gsplat-sogs-data.js calcFocalPoint()。
   *
   * @param result - 输出目标，按本地坐标系写入几何中心
   */
  calcFocalPoint(result: THREE.Vector3): void {
    const { mins, maxs } = this.meta.means

    const map = (v: number) => Math.sign(v) * (Math.exp(Math.abs(v)) - 1)

    result.set(
      (map(mins[0]) + map(maxs[0])) * 0.5,
      (map(mins[1]) + map(maxs[1])) * 0.5,
      (map(mins[2]) + map(maxs[2])) * 0.5
    )
  }

  /**
   * 获取中心点数组（用于排序）。调用后内部缓存即被转移。
   *
   * @returns 长度 numSplats*3 的数组；尚未 generate 或已被取走时返回 null
   */
  getCenters(): Float32Array | null {
    const centers = this._centers
    this._centers = null
    return centers
  }

  prepareCodebook(): void {
    const codebookNames = ['scales', 'sh0', 'shN'] as const
    for (const name of codebookNames) {
      const codebook = this.meta[name]?.codebook as Array<number | null> | undefined
      if (codebook?.[0] === null) {
        codebook[0] = (codebook[1] ?? 0) + ((codebook[1] ?? 0) - (codebook[255] ?? 0)) / 255
      }
    }
  }

  private getCentersRenderResources(renderer: THREE.WebGLRenderer): SogsCentersRenderResources {
    const existing = centersRenderResourcesByRenderer.get(renderer)
    if (existing) {
      return existing
    }

    const material = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      uniforms: {
        means_l: { value: null },
        means_u: { value: null },
        numSplats: { value: 0 },
        means_mins: { value: new THREE.Vector3() },
        means_maxs: { value: new THREE.Vector3() },
      },
      vertexShader: `
        void main() {
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: this.assembleFragmentShader(gsplatSogsCentersFS),
    })

    const scene = new THREE.Scene()
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
    const quad = this.createFullscreenQuad(material)
    const geometry = quad.geometry
    scene.add(quad)

    const resources = { material, geometry, scene, camera }
    centersRenderResourcesByRenderer.set(renderer, resources)
    return resources
  }

  /**
   * 在 GPU 上生成中心点数组，结果缓存到内部并由 getCenters 取出。
   *
   * @param renderer - 用于执行渲染通道的 WebGLRenderer
   */
  async generateCenters(renderer: THREE.WebGLRenderer, signal?: AbortSignal): Promise<void> {
    await runCentersGenerationExclusive(renderer, signal, () =>
      this.generateCentersExclusive(renderer, signal)
    )
  }

  /** 同一个 Renderer 下只允许由 Renderer 级队列调用。 */
  private async generateCentersExclusive(
    renderer: THREE.WebGLRenderer,
    signal?: AbortSignal
  ): Promise<void> {
    throwIfAborted(signal)
    const { width, height } = getTextureImage(this.means_l)

    // 创建 RenderTarget（RGBA32F 用于存储浮点坐标）
    const centersTarget = new THREE.WebGLRenderTarget(width, height, {
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      generateMipmaps: false,
    })

    let material: THREE.ShaderMaterial | null = null
    try {
      const centersRenderResources = this.getCentersRenderResources(renderer)
      material = centersRenderResources.material
      const { scene, camera } = centersRenderResources
      material.uniforms.means_l.value = this.means_l
      material.uniforms.means_u.value = this.means_u
      material.uniforms.numSplats.value = this.numSplats
      material.uniforms.means_mins.value.set(...this.meta.means.mins)
      material.uniforms.means_maxs.value.set(...this.meta.means.maxs)
      throwIfAborted(signal)

      const oldRenderTarget = renderer.getRenderTarget()
      try {
        renderer.setRenderTarget(centersTarget)
        renderer.render(scene, camera)
      } finally {
        renderer.setRenderTarget(oldRenderTarget)
      }
      throwIfAborted(signal)

      // 读回数据
      const centerPixels = await this.readCenterPixels(
        renderer,
        centersTarget,
        width,
        height,
        signal
      )
      throwIfAborted(signal)
      const centers = new Float32Array(this.numSplats * 3)
      let splatIndex = 0

      while (splatIndex < this.numSplats) {
        const src = splatIndex * 4
        const dst = splatIndex * 3
        centers[dst + 0] = centerPixels[src + 0]
        centers[dst + 1] = centerPixels[src + 1]
        centers[dst + 2] = centerPixels[src + 2]
        splatIndex++
      }

      throwIfAborted(signal)
      if (!this._destroyed) {
        this._centers = centers
      }
    } finally {
      try {
        if (material) {
          this.clearCentersRenderUniforms(material)
        }
      } finally {
        centersTarget.dispose()
      }
    }
  }

  private clearCentersRenderUniforms(material: THREE.ShaderMaterial): void {
    const ownsMeansLow = material.uniforms.means_l.value === this.means_l
    const ownsMeansHigh = material.uniforms.means_u.value === this.means_u

    if (ownsMeansLow) {
      material.uniforms.means_l.value = null
    }
    if (ownsMeansHigh) {
      material.uniforms.means_u.value = null
    }
    if (ownsMeansLow && ownsMeansHigh) {
      material.uniforms.numSplats.value = 0
    }
  }

  /**
   * GPU 打包内存（将源纹理打包为紧凑格式）。
   *
   * 参考 PlayCanvas gsplat-sogs-data.js packGpuMemory()，委托给 SogsGpuPackerGPU 执行实际的 MRT 打包。
   * 使用 MRT 同时输出 packedTexture（RGBA32UI）与 packedSh0（RGBA8）。
   *
   * @param renderer - 用于 MRT 打包的 WebGLRenderer
   */
  packGpuMemory(renderer: THREE.WebGLRenderer): void {
    // 创建或复用 GPU 打包器
    if (!this._gpuPacker) {
      this._gpuPacker = new SogsGpuPackerGPU(renderer)
    }

    try {
      // 委托给 SogsGpuPackerGPU 执行打包
      const result = this._gpuPacker.packFromTextures({
        means_l: this.means_l,
        means_u: this.means_u,
        quats: this.quats,
        scales: this.scales,
        sh0: this.sh0,
        sh_labels: this.sh_labels,
        numSplats: this.numSplats,
        meta: this.meta,
      })

      this.packedTexture?.dispose()
      this.packedSh0?.dispose()

      // 保存打包结果
      this.packedTexture = result.packedTexture

      // 使用 MRT 输出的 packedSh0 纹理
      this.packedSh0 = result.packedSh0 as THREE.DataTexture

      // 静默：避免重复日志
    } catch (e) {
      log.warn(`[GSplatSogsData] GPU MRT 打包失败: ${e instanceof Error ? e.message : String(e)}`)
      // 降级方案：不使用打包纹理，直接使用源纹理
      this.packedTexture?.dispose()
      this.packedTexture = null
      this.packedSh0?.dispose()
      this.packedSh0 = null
    }
  }

  /**
   * 打包 SH 高阶系数纹理，参考 PlayCanvas gsplat-sogs-data.js packShMemory()。
   *
   * @param renderer - 用于 SH 重排的 WebGLRenderer
   */
  packShMemory(renderer: THREE.WebGLRenderer): void {
    const { meta, sh_centroids } = this

    if (!sh_centroids) {
      return
    }

    // 关键修复：确保尺寸是整数（ImageBitmap/Canvas 可能返回浮点数）
    const shCentroidsImage = getTextureImage(sh_centroids)
    const width = Math.floor(shCentroidsImage.width)
    const height = Math.floor(shCentroidsImage.height)

    // 准备 uniforms
    const uniforms: Record<string, THREE.IUniform> = {
      sh_centroids: { value: sh_centroids },
    }

    // 准备 defines
    const defines: Record<string, string> = {}

    if (meta.version === 2) {
      // V2 格式：使用 codebook
      if (meta.shN?.codebook) {
        const shNCodebook = this.createCodebookUniform(meta.shN.codebook)
        uniforms.shN_codebook = { value: shNCodebook }
      } else {
        log.warn('[GSplatSogsData.packShMemory] V2 格式但没有 shN.codebook!')
      }
    } else {
      // V1 格式
      defines.REORDER_V1 = '1'
    }

    const fragmentShader = this.assembleFragmentShader(gsplatSogsReorderShFS)

    // 创建 shader
    const material = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      uniforms,
      defines,
      vertexShader: `
        void main() {
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader,
    })

    // 创建渲染目标
    const renderTarget = new THREE.WebGLRenderTarget(width, height, {
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      generateMipmaps: false,
      depthBuffer: false,
      stencilBuffer: false,
    })

    // 渲染全屏四边形
    const quadScene = new THREE.Scene()
    const quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
    const quad = this.createFullscreenQuad(material)
    quadScene.add(quad)

    const oldRenderTarget = renderer.getRenderTarget()
    renderer.setRenderTarget(renderTarget)
    renderer.render(quadScene, quadCamera)
    renderer.setRenderTarget(oldRenderTarget)

    // 关键修复：直接使用 RenderTarget 的纹理作为 packedShN
    // 这样纹理内容已经在 GPU 上，不需要 CPU 回读
    if (this._shRenderTarget) {
      this._shRenderTarget.dispose()
      this._shRenderTarget = null
    } else if (this.packedShN) {
      this.packedShN.dispose()
    }
    this.packedShN = renderTarget.texture
    this.packedShN.name = `sogsPackedShN_${this.url || ''}`

    // 保存 renderTarget 引用，避免被垃圾回收时销毁纹理
    // 注意：这里不 dispose renderTarget，因为我们需要保留它的纹理
    this._shRenderTarget = renderTarget

    // 清理临时资源
    material.dispose()
    quad.geometry.dispose()
  }

  /** 保存 SH RenderTarget 引用，避免纹理被回收 */
  private _shRenderTarget: THREE.WebGLRenderTarget | null = null

  /**
   * 准备 GPU 数据，参考 PlayCanvas gsplat-sog-data.js prepareGpuData()。
   * PlayCanvas 的 SOG 路径只在这里生成 centers，渲染阶段直接读取原始 SOG 纹理。
   *
   * @param renderer - 用于执行渲染通道的 WebGLRenderer
   */
  async prepareGpuData(renderer: THREE.WebGLRenderer): Promise<void> {
    if (this._onContextRestored && this.renderer) {
      this.renderer.domElement.removeEventListener('webglcontextrestored', this._onContextRestored)
    }

    // 保存 renderer 引用
    this.renderer = renderer

    // 1. 清空旧的打包纹理，新的纹理由 packGpuMemory/packShMemory 写入
    this.packedTexture?.dispose()
    this.packedTexture = null
    this.packedSh0?.dispose()
    this.packedSh0 = null

    // packedShN: 由 packShMemory 创建（直接使用 RenderTarget 的纹理）
    // 这里不预先创建，而是在 packShMemory 中创建

    // 注册 WebGL 上下文恢复事件
    this._onContextRestored = this.onContextRestored.bind(this)
    renderer.domElement.addEventListener('webglcontextrestored', this._onContextRestored)

    // 2. Patch codebooks（如果第一个值是 null，使用外插值）
    this.prepareCodebook()

    // 3. 生成中心点
    if (this._centers === null) {
      await this.generateCenters(renderer)
    }

    // Keep this aligned with PlayCanvas GSplatSogData.prepareGpuData():
    // load-time preparation only needs centers because rendering reads raw SOG textures directly.
  }

  /**
   * 创建全屏四边形
   */
  private createFullscreenQuad(material: THREE.Material): THREE.Mesh {
    // 使用 BufferGeometry 创建全屏三角形（比四边形更高效）
    const geometry = new THREE.BufferGeometry()
    const vertices = new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0])
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3))

    return new THREE.Mesh(geometry, material)
  }

  /**
   * 将 codebook 数组转换为 vec4[64] 格式（用于 GLSL uniform）
   */
  private createCodebookUniform(codebook: number[]): number[] {
    // GLSL uniform vec4[64] = 256 个浮点数
    // 每个 vec4 存储 4 个浮点
    const result: number[] = []
    for (let i = 0; i < 64; i++) {
      result.push(codebook[i * 4 + 0] ?? 0)
      result.push(codebook[i * 4 + 1] ?? 0)
      result.push(codebook[i * 4 + 2] ?? 0)
      result.push(codebook[i * 4 + 3] ?? 0)
    }
    return result
  }

  /**
   * 释放资源
   */
  destroy(): void {
    if (this._destroyed) {
      return
    }
    this._destroyed = true
    this.cancelSourceTextureRestore()
    this._centers = null

    const renderer = this.renderer
    const centersResources = renderer ? centersRenderResourcesByRenderer.get(renderer) : undefined
    if (centersResources) {
      this.clearCentersRenderUniforms(centersResources.material)
    }

    // 移除上下文恢复事件监听
    if (this._onContextRestored && renderer) {
      renderer.domElement.removeEventListener('webglcontextrestored', this._onContextRestored)
    }
    this._onContextRestored = null
    this.renderer = null

    // 清理 SH RenderTarget
    const shRenderTarget = this._shRenderTarget
    const packedShN = this.packedShN
    this._shRenderTarget = null
    this.packedShN = null
    if (shRenderTarget) {
      shRenderTarget.dispose()
      releaseTextureImage(packedShN)
    } else {
      disposeTextureAndImage(packedShN)
    }

    const packedTexture = this.packedTexture
    const packedSh0 = this.packedSh0
    this.packedTexture = null
    this.packedSh0 = null

    // 去重后释放，防止调用方复用同一个 Texture 实例时重复触发 dispose/close。
    const textures = new Set<THREE.Texture>([
      this.means_l,
      this.means_u,
      this.quats,
      this.scales,
      this.sh0,
    ])
    if (this.sh_centroids) {
      textures.add(this.sh_centroids)
    }
    if (this.sh_labels) {
      textures.add(this.sh_labels)
    }
    if (packedTexture) {
      textures.add(packedTexture)
    }
    if (packedSh0) {
      textures.add(packedSh0)
    }
    for (const texture of textures) {
      disposeTextureAndImage(texture)
    }
    this._sourceTextureRestoreSources.clear()

    // 清理 GPU 打包器
    if (this._gpuPacker) {
      this._gpuPacker.dispose()
      this._gpuPacker = null
    }
  }

  /**
   * WebGL 上下文恢复时重新修补 codebook。
   * 原始 SOG 纹理由 Three.js 管理，不在这里额外生成 packed 纹理。
   */
  private onContextRestored(): void {
    if (!this.renderer) {
      log.warn('[GSplatSogsData] No renderer available for context restoration')
      return
    }

    this.prepareCodebook()
  }
}
